import {
  FilledInput,
  FilledInputProps,
  OutlinedInput,
  OutlinedInputProps,
  useTheme,
} from "@mui/material";
import { forwardRef, ReactElement } from "react";
import { useFormControl } from "../FormControl/FormControl";

export type TextInputProps =
  | ({ variant: "outlined" } & OutlinedInputProps)
  | ({ variant: "filled" } & FilledInputProps)
  | ({ variant?: never } & OutlinedInputProps);

/**
 * A stand-alone `text` input (or `textarea`).
 *
 * No `helperText`, `cornerHelperText`, `leftCornerElement`, `bottomRightButSlightlyToTheLeftAndUpABit` props.
 * If you want all that fun stuff, use {@link TextField} instead (or don't).
 */
// Match MUI typing for `ref` prop (`any`)
export const TextInput = forwardRef<any, TextInputProps>(
  ({ id: idProp, size: sizeProp, variant: variantProp, ...props }, ref): ReactElement => {
    const theme = useTheme();
    const formControl = useFormControl();
    const id = idProp || formControl?.id;
    const textDefaults = theme.components?.MuiTextField?.defaultProps;
    const controlDefaults = theme.components?.MuiFormControl?.defaultProps;
    const size = sizeProp || formControl?.size || textDefaults?.size || controlDefaults?.size;
    const variant =
      variantProp ||
      (formControl?.variant as "outlined" | "filled") ||
      textDefaults?.variant ||
      controlDefaults?.variant;
    // Only FilledInput takes `hiddenLabel`; OutlinedInput hands it on to its root <div>.
    if (variant === "filled") {
      return <FilledInput {...props} id={id} size={size} ref={ref} hiddenLabel />;
    }
    return <OutlinedInput {...props} id={id} size={size} ref={ref} />;
  },
);
